import { AutogenLayoutArchiveVoidedPayables } from "./autogen/AutogenLayoutArchiveVoidedPayables";
import {
    Button, ChangeEvent, Layout, LookupModelSearchEvent, PanelProcessLastRun, PanelProcessLastRunVariant,
    SlideoutDecorator, Snackbar, StepEvent
} from "@mcleod/components";
import { Alignment, Api, DisplayType, DisplayValue } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { BackOfficeReportPreview } from "./BackOfficeReportPreview";

export class ArchiveVoidedPayables extends AutogenLayoutArchiveVoidedPayables {
    private hasParametersChanged: boolean = false;
    private reportPreviewEditResults: BackOfficeReportPreview;
    private reportPreviewPostResults: BackOfficeReportPreview;
    private processLog: PanelProcessLastRun = PanelProcessLastRun.create("Archive Voided Settlements/Deductions", {
        variant: PanelProcessLastRunVariant.compact
    });
    private isDriverSettlement: boolean;
    private isOffice: boolean;

    async onLoad(): Promise<void> {
        this.initializeEditResultsPanel();
        this.initializePostResultsPanel();

        this.textboxCutoffDate.addChangeListener((event: ChangeEvent) => {
            this.hasParametersChanged = true;
        });
        this.textboxGLDistributionDate.addChangeListener(() => {
            this.hasParametersChanged = true;
        });
        this.textboxPayee.addLookupModelSelectionListener(() => {
            this.hasParametersChanged = true;
        });
        this.buttonArchive.addClickListener(async () => {
            const isYes = await CommonDialogs.showYesNo("Would you like to proceed with the Archive?", "Archive Voided Payables");
            if (!isYes) {
                this.stepEditList.select();
                return;
            }

            this.buttonArchive.busy = true;
            this.stepArchive.busy = true;
            const endpointPath = `lme/${this.isOffice ? "backoffice/officepayroll" : "settlement"}/archive-voided-payables-post`;
            this.runReport(endpointPath, this.reportPreviewPostResults).finally(() => {
                this.reportPreviewPostResults.visible = true;
                this.buttonArchive.busy = false;
                this.buttonArchive.enabled = false;
                this.stepArchive.busy = false;
            });
        });
    }

    async runReport(endpointPath: string, reportPreview: BackOfficeReportPreview) {
        const payees = this.textboxPayee.lookupModelData?.map(lookupModelDataRow => lookupModelDataRow.get("id")).join(",");
        return Api.post(endpointPath, {
            cutoff_date: this.textboxCutoffDate.getDataValue(),
            gl_distribution_date: this.textboxGLDistributionDate.getDataValue(),
            payees: payees,
            is_driver_settlement: this.isDriverSettlement
        }).then((response) => {
            const data = response.data[0];
            const documents = data["documents"];
            if (documents.length === 0) {
                reportPreview.clearData();
                Snackbar.showSnackbar("No voided records to process.");
                this.buttonArchive.enabled = false;
            } else {
                reportPreview.data = documents;
                if (this.stepArchive.selected) {
                    const numArchived = data["num_archived"];
                    Snackbar.showSuccessSnackbar(`Voided payables were successfully archived. (${numArchived})`);
                    this.stepArchive.checked = true;
                    this.processLog?.refreshLastProcessedData();
                } else if (this.stepEditList.selected) {
                    this.stepEditList.checked = true;
                }
            }
        });
    }

    async generateEditList() {
        if (this.hasParametersChanged) {
            this.stepEditList.busy = true;
            const endpointPath = `lme/${this.isOffice ? "backoffice/officepayroll" : "settlement"}/archive-voided-payables-edit`;
            return this.runReport(endpointPath, this.reportPreviewEditResults).finally(() => {
                this.reportPreviewPostResults.clearData();
                this.hasParametersChanged = false;
                this.buttonArchive.enabled = true;
                this.stepEditList.busy = false;
            });
        }
    }

    private initializeEditResultsPanel() {
        if (this.reportPreviewEditResults == null) {
            this.reportPreviewEditResults = new BackOfficeReportPreview("");
            this.stepEditList.add(this.reportPreviewEditResults);
        }
    }

    private initializePostResultsPanel() {
        if (this.reportPreviewPostResults == null) {
            this.reportPreviewPostResults = new BackOfficeReportPreview("", false);
            this.stepArchive.add(this.reportPreviewPostResults);
        }
    }

    private createContinueButton(): Button {
        const buttonContinue: Button = new Button({
            id: "buttonContinue",
            caption: "Continue",
            color: "primary.reverse",
            backgroundColor: "primary",
            height: 34,
            rowBreak: false,
            onClick: async () => {
                this.stepperArchiveVoidedPayables.selectedIndex = this.stepperArchiveVoidedPayables.selectedIndex + 1;
            }
        });
        buttonContinue.borderWidth = 0;
        return buttonContinue;
    }

    private async doBeforeStep(event: StepEvent, buttonContinue: Button) {
        if (event.newStep == this.stepEditList) {
            await this.generateEditList();
            if (this.reportPreviewEditResults.isEmpty()) {
                this.stepParameters.select();
                return;
            }
            this.stepParameters.checked = true;
        }
        buttonContinue.visible = event.newStep != this.stepArchive;
    }

    public static showSlideout(props: { cutoff_date: Date, is_driver_settlement?: boolean, is_office?: boolean }) {
        const layout = Layout.getLayout("lme/settlement/ArchiveVoidedPayables") as ArchiveVoidedPayables;
        const addlComponents = [];
        if (layout.processLog) {
            if (props.is_office) {
                layout.processLog = PanelProcessLastRun.create("Archive Voided Office Deductions", {
                    variant: PanelProcessLastRunVariant.compact
                });
            }
            addlComponents.push(layout.processLog);
        }
        const buttonContinue = layout.createContinueButton();
        addlComponents.push(buttonContinue);
        SlideoutDecorator.decorateAndSlideIn(layout, {
            width: "60%",
            title: props.is_office ? "Archive Voided Pay Adjustments" : "Archive Voided Settlements/Pay Adjustments",
            fillVerticalSpace: true,
            transitionOptions: {
                direction: Alignment.RIGHT
            },
            addlComponents: addlComponents,
            layoutLoadListeners: () => {
                layout.stepperArchiveVoidedPayables.addBeforeStepListener(async (event: StepEvent) => {
                    await layout.doBeforeStep(event, buttonContinue);
                });
                layout.textboxCutoffDate.text =
                    DisplayValue.getDateDisplayValue(props.cutoff_date ?? new Date(), DisplayType.DATE);
                layout.isOffice = props.is_office;
                layout.isDriverSettlement = props.is_driver_settlement;
                if (layout.isOffice) {
                    layout.textboxPayee.lookupModelLayout = "lme/settlement/officepayroll/OfficePayeeLookup";
                    layout.textboxPayee.quickInfoLayout = "lme/settlement/officepayroll/OfficePayeeQuickInfo";
                    layout.textboxGLDistributionDate.visible = false;
                }
                else {
                    layout.textboxPayee.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
                        event.filter["drs_payee.type_of"] = props.is_driver_settlement ? "in C,O" : "in A,R";
                    });
                }
            }
        });
    }
}
